/**
 * "AI-samenvatting van vandaag" (dashboard): the app gathers today's tasks,
 * upcoming meetings and recently edited pages itself (only pages visible to
 * AI), and Claude turns them into a short briefing in one request.
 */
import { addDays, splitDateTime } from '@nexus/core';
import type { Store } from '@nexus/store';
import { loadTasks } from '@nexus/workspace';
import type { AiModel, AiUsage, MessageParam } from './protocol';
import { AiError, callOnce, type Transport } from './chat';
import { tasksInScope, toolContext } from './tools';

const MAX_TASKS = 25;
const MAX_RECENT = 8;

export async function summaryMessage(store: Store, now = new Date()): Promise<MessageParam> {
  const ctx = await toolContext(store, now);
  const { today, scope } = ctx;
  const tasks = (await loadTasks(store)).filter((t) => scope.visible.has(t.pageId));
  const line = (t: (typeof tasks)[number]) =>
    `- ${t.title.replace(/\s+/g, ' ')}${t.due ? ` (due ${t.due}${t.dueTime ? ` ${t.dueTime}` : ''})` : ''}${t.priority ? ` [${t.priority}]` : ''} in [[${t.sourceTitle}]]`;
  const section = (name: string, list: typeof tasks) =>
    `${name} (${list.length}):\n${list.slice(0, MAX_TASKS).map(line).join('\n') || '- none'}`;

  const overdue = tasksInScope(tasks, 'overdue', today) ?? [];
  const dueToday = (tasksInScope(tasks, 'today', today) ?? []).filter((t) => !overdue.includes(t));
  const week = (tasksInScope(tasks, 'week', today) ?? []).filter(
    (t) => !overdue.includes(t) && !dueToday.includes(t),
  );

  // Meetings in the Vergaderingen database from today until the day after tomorrow.
  const meetingsId = await store.getSetting('meetings_db_id');
  const until = addDays(today, 2);
  const meetings: string[] = [];
  if (meetingsId && scope.visible.has(meetingsId)) {
    for (const p of scope.visible.values()) {
      if (p.parentId !== meetingsId) continue;
      const raw = Object.entries(p.props).find(([k]) => /^(date|datum)$/i.test(k))?.[1];
      const when = typeof raw === 'string' ? splitDateTime(raw) : null;
      if (when && when.date >= today && when.date <= until)
        meetings.push(`- ${when.date}${when.time ? ` ${when.time}` : ''}: [[${p.title}]]`);
    }
    meetings.sort();
  }

  const recent = [...scope.visible.values()]
    .filter((p) => p.parentId !== meetingsId)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_RECENT)
    .map((p) => `- [[${p.title || 'Untitled'}]]`);

  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(now);
  return {
    role: 'user',
    content: [
      `Today is ${weekday} ${today}.`,
      section('Overdue tasks', overdue),
      section('Due or scheduled today', dueToday),
      section('Due later this week', week),
      `Meetings until ${until}:\n${meetings.join('\n') || '- none'}`,
      `Recently edited pages:\n${recent.join('\n') || '- none'}`,
    ].join('\n\n'),
  };
}

export async function runSummary(opts: {
  transport: Transport;
  model: AiModel;
  message: MessageParam;
  signal?: AbortSignal;
  onText?: (soFar: string) => void;
  onUsage?: (usage: AiUsage) => void;
}): Promise<string> {
  let text = '';
  const done = await callOnce(
    opts.transport,
    { mode: 'summary', model: opts.model, messages: [opts.message] },
    opts.signal,
    {
      onText: (t) => {
        text += t;
        opts.onText?.(text);
      },
      onUsage: opts.onUsage,
    },
  );
  if (done.stop_reason === 'refusal') throw new AiError('refusal');
  return text.trim();
}
